/*
	The calls to the PHP backend, on fetch.

	A non-2xx status rejects with an ApiError, so callers branch on the status
	and the decoded body the same way whether the server refused the request or
	the network did.
 */

export class ApiError extends Error {
	constructor(
		public readonly status: number,
		public readonly data: any,
		public readonly url: string,
	) {
		super('API ' + status + ': ' + url);
		this.name = 'ApiError';
	}
}

export type ApiResponse<T = any> = Readonly<{
	data: T,
	status: number,
}>;

/* One log line per failed call, in the same shape everywhere. */
export function logApiError(error: unknown): void {
	if (error instanceof ApiError) {
		console.log('API ' + error.status + ':', error.url);
	} else {
		console.log('API request failed:', error);
	}
}

/* JSON when the server says so, the raw text otherwise, undefined for an empty body. */
async function decode(response: Response): Promise<any> {
	const text = await response.text();

	if (text === '') {
		return undefined;
	}

	const contentType = response.headers.get('content-type') ?? '';

	return contentType.includes('json') ? JSON.parse(text) : text;
}

async function request<T = any>(url: string, init: RequestInit = {}): Promise<ApiResponse<T>> {
	const response = await fetch(url, {
		...init,
		headers: { Accept: 'application/json', ...(init.headers ?? {}) },
	});
	const data = await decode(response);

	if (!response.ok) {
		throw new ApiError(response.status, data, url);
	}

	return { data, status: response.status };
}

export function apiGet<T = any>(url: string): Promise<ApiResponse<T>> {
	return request<T>(url);
}

export function apiPost<T = any>(url: string, body?: unknown): Promise<ApiResponse<T>> {
	if (body === undefined) {
		return request<T>(url, { method: 'POST' });
	}

	return request<T>(url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
	});
}
